import { createTheme } from "@mui/material/styles";

// Central application theme — controls palette, typography, shape, and
// component defaults so the whole admin app looks consistent and modern.
const theme = createTheme({
  palette: {
    mode: "light",
    primary: { main: "#4f46e5", light: "#818cf8", dark: "#3730a3", contrastText: "#fff" },
    secondary: { main: "#db2777", light: "#f472b6", dark: "#9d174d" },
    success: { main: "#059669", light: "#34d399", dark: "#047857" },
    warning: { main: "#d97706", light: "#fbbf24", dark: "#b45309" },
    error: { main: "#dc2626", light: "#f87171", dark: "#b91c1c" },
    info: { main: "#0891b2", light: "#22d3ee", dark: "#0e7490" },
    background: { default: "#eef1f8", paper: "#ffffff" },
    text: { primary: "#0f172a", secondary: "#64748b" },
    divider: "rgba(15,23,42,0.08)",
  },
  shape: { borderRadius: 12 },
  typography: {
    fontFamily:
      '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
    h3: { fontWeight: 800, letterSpacing: "-0.03em" },
    h4: { fontWeight: 800, letterSpacing: "-0.02em" },
    h5: { fontWeight: 700, letterSpacing: "-0.01em" },
    h6: { fontWeight: 700 },
    subtitle1: { fontWeight: 600 },
    subtitle2: { fontWeight: 600 },
    button: { textTransform: "none", fontWeight: 600 },
    overline: { fontWeight: 700, letterSpacing: "0.08em" },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          // Subtle two-tone radial wash behind the whole app
          backgroundColor: "#eef1f8",
          backgroundImage:
            "radial-gradient(1000px 500px at 100% -5%, rgba(79,70,229,0.10) 0%, transparent 55%), radial-gradient(900px 500px at 0% 110%, rgba(219,39,119,0.08) 0%, transparent 55%)",
          backgroundAttachment: "fixed",
        },
        "*::-webkit-scrollbar": { width: 10, height: 10 },
        "*::-webkit-scrollbar-thumb": {
          backgroundColor: "rgba(100,116,139,0.35)",
          borderRadius: 8,
          border: "2px solid transparent",
          backgroundClip: "content-box",
        },
        "*::-webkit-scrollbar-thumb:hover": {
          backgroundColor: "rgba(100,116,139,0.55)",
        },
      },
    },
    MuiAppBar: {
      defaultProps: { color: "inherit", elevation: 0 },
      styleOverrides: {
        root: {
          backgroundColor: "rgba(255,255,255,0.72)",
          backdropFilter: "blur(12px)",
          color: "#0f172a",
          borderBottom: "1px solid rgba(15,23,42,0.06)",
          boxShadow: "none",
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        rounded: { borderRadius: 16 },
        outlined: { borderColor: "rgba(15,23,42,0.08)" },
        elevation1: {
          boxShadow:
            "0 1px 2px rgba(15,23,42,0.04), 0 4px 12px rgba(15,23,42,0.05)",
        },
        elevation2: { boxShadow: "0 6px 18px rgba(15,23,42,0.07)" },
        elevation3: { boxShadow: "0 12px 32px rgba(15,23,42,0.09)" },
      },
    },
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: {
          borderRadius: 10,
          paddingInline: 16,
          transition: "transform 0.12s ease, box-shadow 0.12s ease",
        },
        containedPrimary: {
          background: "linear-gradient(135deg,#4f46e5,#7c3aed)",
          "&:hover": {
            background: "linear-gradient(135deg,#4338ca,#6d28d9)",
            boxShadow: "0 8px 20px rgba(79,70,229,0.35)",
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          border: "1px solid rgba(15,23,42,0.06)",
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: { fontWeight: 600, borderRadius: 8 },
        sizeSmall: { height: 22 },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          "& .MuiTableCell-head": {
            fontWeight: 700,
            fontSize: "0.75rem",
            textTransform: "uppercase",
            letterSpacing: "0.04em",
            color: "#64748b",
            backgroundColor: "#f8fafc",
            borderBottom: "1px solid rgba(15,23,42,0.08)",
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: { borderBottom: "1px solid rgba(15,23,42,0.05)" },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          transition: "background-color 0.15s ease",
          "&:hover": { backgroundColor: "rgba(79,70,229,0.04)" },
          "&:last-of-type td": { borderBottom: 0 },
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: { root: { borderRadius: 10, margin: "2px 10px" } },
    },
    MuiTextField: { defaultProps: { size: "small" } },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          backgroundColor: "#fff",
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderWidth: 2,
          },
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: { textTransform: "none", fontWeight: 600, minHeight: 44 },
      },
    },
    MuiTabs: {
      styleOverrides: { indicator: { height: 3, borderRadius: 3 } },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: "#0f172a",
          fontSize: "0.75rem",
          borderRadius: 8,
          padding: "6px 10px",
        },
      },
    },
    MuiDialog: {
      styleOverrides: { paper: { borderRadius: 16 } },
    },
  },
});

export default theme;
